import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { ArrowLeft, Target, DollarSign, TrendingUp, Users, Calendar as CalendarIcon, Sparkles, AlertCircle, Film, FileText, Smartphone, Mail, ListChecks, CheckCircle2, Clock, Video } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const PIECE_ICONS = { brief: FileText, script: Film, calendar: CalendarIcon, push: Smartphone, email: Mail, task: ListChecks, video: Video };
const STATUS_COLOR = {
  publicado: "bg-green-100 text-green-700",
  aprobado: "bg-green-100 text-green-700",
  enviado: "bg-green-100 text-green-700",
  programado: "bg-blue-50 text-blue-700",
  en_revision: "bg-blue-50 text-blue-700",
  en_produccion: "bg-amber-50 text-amber-700",
  borrador: "bg-gray-100 text-gray-600",
  idea: "bg-gray-100 text-gray-600",
};

const CampaignCommand = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState("");
  const [loadingInsights, setLoadingInsights] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/command/campaign/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Campaña no encontrada")).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const loadInsights = async () => {
    setLoadingInsights(true);
    try {
      const r = await api.get(`/command/campaign/${id}/aura-insights`);
      setInsights(r.data.narrative || "");
    } catch (e) {
      toast.error("Error generando insights");
    } finally { setLoadingInsights(false); }
  };

  if (loading) return <Layout title="Cargando..."><div className="flex justify-center py-20"><span className="spinner" /></div></Layout>;
  if (!data) return <Layout title="Error"><p>Campaña no encontrada</p></Layout>;

  const c = data.campaign;
  const perf = data.performance;
  const stats = data.stats;

  // Compute date label: future, in-progress, ended
  let dateLabel = null;
  if (c.start_date && c.end_date) {
    const today = new Date().toISOString().slice(0, 10);
    if (c.start_date > today) {
      const daysToStart = Math.ceil((new Date(c.start_date) - new Date()) / (1000 * 60 * 60 * 24));
      dateLabel = { text: `Inicia en ${daysToStart} días`, cls: "pill-blue" };
    } else if (c.end_date >= today) {
      dateLabel = { text: `${data.days_remaining ?? 0} días restantes`, cls: data.days_remaining !== null && data.days_remaining < 7 ? "pill-red" : "pill-blue" };
    } else {
      dateLabel = { text: "Campaña terminada", cls: "pill-gray" };
    }
  } else if (data.days_remaining !== null) {
    dateLabel = { text: `${data.days_remaining} días restantes`, cls: data.days_remaining < 7 ? "pill-red" : "pill-blue" };
  }

  return (
    <Layout title={c.name} subtitle="🎯 Comando de Misión">
      <Link to="/campaigns" className="text-xs text-[#666] hover:text-[#0A0A0A] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Volver a campañas
      </Link>

      {/* HERO */}
      <div className="aura-card-flat p-6 mb-5 bg-gradient-to-br from-white to-[#FFF9E6]" data-testid="campaign-hero">
        <div className="flex justify-between items-start mb-4 gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-14 h-14 rounded-2xl flex-shrink-0" style={{ background: c.color }} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`pill ${STATUS_COLOR[c.status] || "pill-gray"}`}>{c.status}</span>
                {dateLabel && (
                  <span className={`pill text-[10px] ${dateLabel.cls}`}>
                    <Clock size={10} className="inline mr-0.5" /> {dateLabel.text}
                  </span>
                )}
                {data.pending_approvals > 0 && (
                  <Link to="/aprobaciones" className="pill pill-yellow text-[10px]">
                    {data.pending_approvals} aprobaciones pendientes
                  </Link>
                )}
              </div>
              <h2 className="font-display text-3xl font-medium">{c.name}</h2>
              {c.objective && <p className="text-sm text-[#666] mt-1">{c.objective}</p>}
              {c.start_date && (
                <p className="text-xs text-[#666] mt-1">{c.start_date} → {c.end_date || "—"}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="label-overline">Avance</div>
            <div className="font-display text-5xl font-medium leading-none">{data.progress}%</div>
            <div className="w-32 h-1.5 bg-[#E5E5E5] rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-[#D99D26]" style={{ width: `${data.progress}%` }} />
            </div>
          </div>
        </div>

        {/* KPIs row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <KpiBox icon={DollarSign} label="Presupuesto" value={c.budget_mxn ? `$${c.budget_mxn.toLocaleString("es-MX")}` : "—"} sub="MXN" />
          <KpiBox icon={Target} label="Alcance meta" value={c.reach_target ? c.reach_target.toLocaleString("es-MX") : "—"} sub={perf.total_reach ? `Real: ${perf.total_reach.toLocaleString("es-MX")}` : ""} />
          <KpiBox icon={TrendingUp} label="ROAS meta" value={c.roas_target ? `${c.roas_target}x` : "—"} sub="" />
          <KpiBox icon={Sparkles} label="Engagement promedio" value={perf.avg_engagement ? `${perf.avg_engagement}%` : "—"} sub="posts publicados" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        {/* MAIN: pieces */}
        <div className="space-y-5">
          {/* Pipeline summary */}
          <div className="aura-card-flat p-5">
            <div className="label-overline mb-3">Pipeline de la campaña</div>
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
              <Stat icon={FileText} label="Briefs" value={stats.briefs} />
              <Stat icon={Film} label="Guiones" value={stats.scripts} />
              <Stat icon={ListChecks} label="Tareas" value={stats.tasks_pending} sub="pendientes" />
              <Stat icon={CalendarIcon} label="Items" value={stats.calendar_items} sub="calendario" />
              <Stat icon={Smartphone} label="Push" value={stats.push_count} />
              <Stat icon={Mail} label="Emails" value={stats.email_count} />
              <Stat icon={CheckCircle2} label="Publicados" value={stats.posts_published} highlight />
            </div>
          </div>

          {/* Performance */}
          {(perf.viral + perf.normal + perf.no_funciono > 0) && (
            <div className="aura-card-flat p-5">
              <div className="label-overline mb-3">🧠 Rendimiento (marcado manualmente)</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <div className="text-3xl font-display font-medium text-green-700">{perf.viral}</div>
                  <div className="text-[10px] text-green-700">🔥 Virales</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                  <div className="text-3xl font-display font-medium text-gray-700">{perf.normal}</div>
                  <div className="text-[10px] text-gray-700">➖ Normales</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <div className="text-3xl font-display font-medium text-red-700">{perf.no_funciono}</div>
                  <div className="text-[10px] text-red-700">❌ Falla</div>
                </div>
              </div>
            </div>
          )}

          {/* Pieces lists */}
          <PieceList title="📋 Briefs" items={data.briefs} fields={["title", "type", "assignee_name", "status"]} deepLink="/briefs" testid="brief-list" />
          <PieceList title="🎬 Guiones" items={data.scripts} fields={["title", "platform", "creator_name", "status"]} deepLink="/guiones" testid="script-list" />
          <PieceList title="📅 Calendario de la campaña" items={data.calendar_items} fields={["title", "date", "platform", "status"]} deepLink="/calendario" testid="cal-list" />
        </div>

        {/* SIDE: team + insights */}
        <aside className="space-y-4">
          {/* Aura insights */}
          <div className="aura-card-flat p-5 bg-gradient-to-br from-[#FFF9E6] to-white border border-[#D99D26]/20">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <div className="aura-logo-mark w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold">A</div>
                <div className="font-display text-sm font-medium">Aura · Asesor estratégico</div>
              </div>
              <button onClick={loadInsights} disabled={loadingInsights} className="btn-outline text-xs" data-testid="aura-insights-btn">
                {loadingInsights ? <span className="spinner" /> : insights ? "Actualizar" : "Generar"}
              </button>
            </div>
            {insights ? (
              <div className="text-xs markdown-content max-h-80 overflow-y-auto" data-testid="aura-insights">
                <Markdown content={insights} />
              </div>
            ) : (
              <p className="text-xs text-[#666]">Aura analiza estado vs. objetivos, riesgos, acciones próximas 72h.</p>
            )}
          </div>

          {/* Team */}
          <div className="aura-card-flat p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-[#8a5e10]" />
              <div className="font-display text-sm font-medium">Equipo asignado</div>
            </div>
            {data.team.length === 0 ? (
              <p className="text-xs text-[#666]">Nadie asignado aún.</p>
            ) : (
              <div className="space-y-2">
                {data.team.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 text-xs" data-testid={`team-${u.id}`}>
                    <div className="w-8 h-8 rounded-full bg-[#D99D26]/15 flex items-center justify-center text-[#8a5e10] font-medium text-[11px]">
                      {(u.name || u.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.name}</div>
                      <div className="text-[10px] text-[#666]">{u.role.replace(/_/g, " ")}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Channels & branches */}
          <div className="aura-card-flat p-5">
            <div className="label-overline mb-2">Canales y sucursales</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(c.channels || []).map((ch) => <span key={ch} className="pill pill-gray text-[10px]">{ch}</span>)}
            </div>
            <div className="flex flex-wrap gap-1">
              {(c.branches || []).map((b) => <span key={b} className="pill pill-yellow text-[10px]">📍 {b}</span>)}
            </div>
          </div>
        </aside>
      </div>
    </Layout>
  );
};

const KpiBox = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white rounded-xl border border-[#E5E5E5] p-3">
    <div className="text-[10px] text-[#666] flex items-center gap-1 mb-1"><Icon size={11} /> {label}</div>
    <div className="font-display text-xl font-medium leading-tight">{value}</div>
    {sub && <div className="text-[10px] text-[#666] mt-0.5">{sub}</div>}
  </div>
);

const Stat = ({ icon: Icon, label, value, sub, highlight }) => (
  <div className={`text-center p-3 rounded-xl ${highlight ? "bg-green-50 border border-green-200" : "bg-[#FAFAFA]"}`}>
    <Icon size={14} className="mx-auto text-[#8a5e10] mb-1" />
    <div className="font-display text-xl font-medium">{value}</div>
    <div className="text-[10px] text-[#666]">{label}</div>
    {sub && <div className="text-[9px] text-[#999]">{sub}</div>}
  </div>
);

const PieceList = ({ title, items, fields, deepLink, testid }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="aura-card-flat p-5" data-testid={testid}>
      <div className="flex justify-between items-center mb-3">
        <div className="font-display text-sm font-medium">{title} <span className="text-[#666]">({items.length})</span></div>
        <Link to={deepLink} className="text-xs text-[#8a5e10] font-medium">Ver todos →</Link>
      </div>
      <div className="space-y-2">
        {items.slice(0, 5).map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-2 text-sm py-2 border-b border-[#F0F0F0] last:border-0">
            <div className="flex-1 min-w-0 truncate">
              <div className="font-medium truncate">{it.title || it.subject || "(sin título)"}</div>
              <div className="text-[10px] text-[#666] flex gap-2">
                {it.date && <span>{it.date}</span>}
                {it.platform && <span>· {it.platform}</span>}
                {it.creator_name && <span>· {it.creator_name}</span>}
                {it.assignee_name && <span>· {it.assignee_name}</span>}
              </div>
            </div>
            <span className={`pill text-[9px] ${STATUS_COLOR[it.status] || "pill-gray"}`}>{it.status?.replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignCommand;
